/**
 * Local saves. Prototype only — the backend milestone moves this to a
 * VoidMiner-owned Supabase project (GDD §10), never IdleVoid's.
 */

import { newGame, newRun, SAVE_VERSION, type GameState } from './sim/state';

const KEY = 'voidminer.save.v1';

/** Fills keys a newer build added, so old saves keep loading. */
function fill<T>(base: T, saved: unknown): T {
  if (Array.isArray(base)) return (Array.isArray(saved) ? saved : base) as T;
  if (base === null || typeof base !== 'object') return (saved === undefined || saved === null ? base : saved) as T;
  if (saved === null || typeof saved !== 'object' || Array.isArray(saved)) return base;
  const out: Record<string, unknown> = { ...(saved as Record<string, unknown>) };
  for (const [key, value] of Object.entries(base as Record<string, unknown>)) {
    out[key] = fill(value, (saved as Record<string, unknown>)[key]);
  }
  return out as T;
}

export function loadGame(): GameState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return newGame();
    const parsed = JSON.parse(raw) as Partial<GameState>;
    // v1 carried raid and Defence state; prototype saves before v5 simply start over.
    if ((parsed.version ?? 0) < 5) return newGame();
    // v5 → v6: the Matrix went from a list of allocated nodes to a level per node.
    if (Array.isArray(parsed.matrix)) {
      parsed.matrix = Object.fromEntries((parsed.matrix as string[]).map((id) => [id, 1]));
    }
    const base = newGame();
    const state = fill(base, parsed);
    const fresh = newRun(state.run?.drift ?? null);
    state.run = fill(fresh, parsed.run);
    // Content grows (new planets); pad per-planet state so a save from a smaller
    // galaxy loads into a bigger one instead of reading past the end.
    for (let i = state.run.planets.length; i < fresh.planets.length; i += 1) state.run.planets.push(fresh.planets[i]!);
    state.version = SAVE_VERSION;
    return state;
  } catch {
    return newGame();
  }
}

export function saveGame(state: GameState): void {
  state.savedAt = Date.now();
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode or full storage: the session keeps playing, it just won't persist.
  }
}

export function wipeSave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to remove.
  }
}
